import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCognito } from "../../../contexts/CognitoProvider";
import { useWebSocket } from "../../../contexts/WebSocketProvider";
import { useRealtimeUpdates } from "../../../hooks/useRealtimeUpdates";
import {
	austracTypeToWorkflow,
	c2EntityTypeToWorkflow,
	cleanEntityCode,
	determineWorkflow,
	formatPhoneNumber,
	isValidABN,
	isValidACN,
	riskRatingToEnhancedProfile,
	type EntityCodeType,
	type RegistrationResponse,
	type WorkflowType,
} from "../../../services/registrationApi";
import { socketService } from "../../../services/socketService";
import wsApi from "../../../services/wsApi";
import useInvitationsStore from "../../../stores/invitationsStore";

import type { Invitation } from "../../../types/invitations.types";
import { RISK_BADGE_CONFIG } from "../../../types/invitations.types";
import type { SavedAssessment } from "../../../types/risk-assessment.types";

import {
	formatABN,
	formatACN,
	lookupABN,
	lookupACN,
	type ABNLookupResult,
	type ACNLookupResult,
} from "../../../services/abnLookupService";
import {
	STORAGE_KEY_ASSESSMENTS,
	findAssessmentInList,
} from "../../../utils/assessmentHelpers";
import { formatDateShort } from "../../../utils/dateFormatters";
import { mapRiskRecordsToAssessments } from "../../../utils/riskRecordMapper";

// ─── Helper ────────────────────────────────────────────────────────────────────

/**
 * Map a risk-assessment c2EntityType to the invitation wizard's entitySubType.
 */
export function c2EntityTypeToSubType(
	c2EntityType: string,
): "sole-trader" | "company" | "partnership" | "trust" | "association" | "" {
	const t = c2EntityType.toLowerCase();
	if (t === "individual") return "";
	if (t === "sole_trader") return "sole-trader";
	if (t === "body_corporate") return "company";
	if (t === "trust") return "trust";
	if (t === "government") return "association";
	return "company";
}

/** Map the server invitation row while preserving attribution metadata. */
export function mapInvitationRecords(records: any[]): Invitation[] {
	return records
		.filter((record) => !!(record.entityName || record.organisationName))
		.map((record) => ({
			id: record.invitationId || record.id,
			email:
				record.recipientEmail ||
				record.email ||
				record.clientRef ||
				record.entityAbn ||
				"",
			organisationName:
				record.entityName || record.organisationName || "Unknown",
			role: record.role || "Pending",
			sentDate: formatDateShort(record.sentAt || record.createdAt),
			status: (record.status || "pending").toLowerCase(),
			expiryDate: formatDateShort(record.expiresAt),
			createdByName: record.createdByName?.trim() || undefined,
			createdBy: record.createdBy || undefined,
			createdDate: formatDateShort(record.createdAt || record.sentAt),
			c2Username:
				record.c2Username ||
				(record.cddLink ? record.cddLink.split("/").pop() : undefined),
			tempPassword:
				record.c2TempPassword || record.tempPassword || undefined,
		}));
}

// ─── Custom hook ───────────────────────────────────────────────────────────────

export function useInvitationsState() {
	const navigate = useNavigate();
	const location = useLocation();
	const { user } = useCognito();

	// Zustand store
	const storeInvitations = useInvitationsStore((state) => state.allInvitations);
	const addInvitationToStore = useInvitationsStore(
		(state) => state.addInvitation,
	);
	const setStoreInvitations = useInvitationsStore(
		(state) => state.setInvitations,
	);

	// Wait for WebSocket authentication before fetching
	const { isAuthenticated: wsAuthenticated } = useWebSocket();

	// ── State declarations ───────────────────────────────────────────────
	const [invitations, setInvitations] = useState<Invitation[]>([]);
	const [loadingInvitations, setLoadingInvitations] = useState(true);

	const [showInviteForm, setShowInviteForm] = useState(false);
	const [newInvite, setNewInvite] = useState({
		email: "",
		organisationName: "",
		generatedLink: "",
	});

	const [registrationData, setRegistrationData] = useState({
		firstName: "",
		middleName: "",
		lastName: "",
		email: "",
		phone: "",
		dateOfBirth: "",
		legalEntityCodeType: "ABN" as EntityCodeType,
		legalEntityCodeData: "",
		workflow: "INDIVIDUAL" as WorkflowType,
	});

	const [registrationResponse, setRegistrationResponse] =
		useState<RegistrationResponse | null>(null);
	const [isRegistering, setIsRegistering] = useState(false);

	const [inviteMessage, setInviteMessage] = useState("");
	const [filterStatus, setFilterStatus] = useState<
		"all" | "pending" | "accepted" | "declined" | "expired"
	>("all");
	const [searchTerm, setSearchTerm] = useState("");

	const [formAssessment, setFormAssessment] = useState<SavedAssessment | null>(
		null,
	);
	const [assessedClients, setAssessedClients] = useState<SavedAssessment[]>([]);
	const [loadingAssessments, setLoadingAssessments] = useState(false);

	const [isLookingUp, setIsLookingUp] = useState(false);
	const [abnLookupResult, setAbnLookupResult] = useState<
		ABNLookupResult | ACNLookupResult | null
	>(null);
	const [abnLookupError, setAbnLookupError] = useState("");

	const [isIndividual, setIsIndividual] = useState(false);
	const [wizardStep, setWizardStep] = useState(1);
	const [entitySubType, setEntitySubType] = useState<
		"sole-trader" | "company" | "partnership" | "trust" | "association" | ""
	>("");

	// ── Helpers ──────────────────────────────────────────────────────────

	const mapRecordsToInvitations = useCallback(mapInvitationRecords, []);

	const fetchInvitationsFromBackend = useCallback(async () => {
		try {
			setLoadingInvitations(true);
			const records = await wsApi.invitationGetAll();
			const mapped = mapRecordsToInvitations(records);
			setInvitations(mapped);
			setStoreInvitations(mapped);
		} catch (_err) {
			// ignore
		} finally {
			setLoadingInvitations(false);
		}
	}, [mapRecordsToInvitations, setStoreInvitations]);

	// ── Effects ──────────────────────────────────────────────────────────

	// Auto-open invite form when navigated from Risk Assessment
	useEffect(() => {
		const state = location.state as {
			fromRiskAssessment?: boolean;
			c2Name?: string;
			c2EntityType?: string;
			c2Abn?: string;
			abnCodeType?: string;
			entitySubType?: string;
			riskRating?: string;
			assessmentId?: string;
			contactFirst?: string;
			contactMiddle?: string;
			contactLast?: string;
			contactEmail?: string;
			contactPhone?: string;
		} | null;
		if (state?.fromRiskAssessment && state?.c2Name) {
			setShowInviteForm(true);
			setNewInvite({
				email: "",
				organisationName: state.c2Name,
				generatedLink: "",
			});
			if (state.c2EntityType) {
				const workflow = c2EntityTypeToWorkflow(state.c2EntityType);
				const isIndiv = state.c2EntityType.toLowerCase() === "individual";
				const subType =
					(state.entitySubType as any) ||
					c2EntityTypeToSubType(state.c2EntityType);
				setIsIndividual(isIndiv);
				setEntitySubType(subType);
				const nameParts = (state.c2Name ?? "").trim().split(/\s+/);
				const hasStructuredName = !!(state.contactFirst || state.contactLast);
				const prefillFirst = isIndiv ? (state.contactFirst || nameParts[0] || "") : (state.contactFirst ?? "");
				const prefillMiddle = isIndiv
					? (state.contactMiddle || (!hasStructuredName && nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : ""))
					: (state.contactMiddle ?? "");
				const prefillLast = isIndiv
					? (state.contactLast || (nameParts.length > 1 ? nameParts[nameParts.length - 1] : ""))
					: (state.contactLast ?? "");
				setRegistrationData((prev) => ({
					...prev,
					workflow,
					legalEntityCodeType: (state.abnCodeType || "ABN") as EntityCodeType,
					legalEntityCodeData: isIndiv ? "" : (state.c2Abn ?? ""),
					...(prefillFirst ? { firstName: prefillFirst } : {}),
					...(prefillMiddle ? { middleName: prefillMiddle } : {}),
					...(prefillLast ? { lastName: prefillLast } : {}),
					...(state.contactEmail ? { email: state.contactEmail } : {}),
					...(state.contactPhone ? { phone: state.contactPhone } : {}),
				}));
				setWizardStep(2);
			}
			window.history.replaceState({}, document.title);

			setLoadingAssessments(true);
			const navEntitySubType = ((state.entitySubType as string) ||
				(state.c2EntityType
					? c2EntityTypeToSubType(state.c2EntityType)
					: "")) as
				| ""
				| "sole-trader"
				| "company"
				| "partnership"
				| "trust"
				| "association";
			wsApi
				.riskAssessmentGetAll()
				.then((records) => {
					const mapped = mapRiskRecordsToAssessments(records);
					setAssessedClients(mapped);
					localStorage.setItem(STORAGE_KEY_ASSESSMENTS, JSON.stringify(mapped));
					const exact =
						(state.assessmentId
							? mapped.find((a) => a.id === state.assessmentId)
							: null) ??
						mapped.find((a) => a.clientName === state.c2Name) ??
						null;
					if (exact) {
						setFormAssessment(exact);
						setNewInvite({
							email: exact.clientRef || exact.entityAbn || "",
							organisationName: state.c2Name!,
							generatedLink: "",
						});
						if (exact.c2EntityType || state.c2EntityType) {
							const effectiveType = exact.c2EntityType || state.c2EntityType!;
							const workflow = c2EntityTypeToWorkflow(effectiveType);
							const isIndiv = effectiveType.toLowerCase() === "individual";
							setIsIndividual(isIndiv);
							setEntitySubType(isIndiv ? "" : navEntitySubType);
							const fullName = (exact.clientName ?? state.c2Name ?? "").trim();
							const nameParts = fullName.split(/\s+/);
							const hasStructuredName = !!(state.contactFirst || state.contactLast);
							const prefillFirst = isIndiv ? (state.contactFirst || nameParts[0] || "") : (state.contactFirst ?? "");
							const prefillMiddle = isIndiv
								? (state.contactMiddle || (!hasStructuredName && nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : ""))
								: (state.contactMiddle ?? "");
							const prefillLast = isIndiv
								? (state.contactLast || (nameParts.length > 1 ? nameParts[nameParts.length - 1] : ""))
								: (state.contactLast ?? "");
							const prefillEmail = state.contactEmail || (exact.clientRef?.includes("@") ? exact.clientRef : "");
							const prefillPhone = state.contactPhone ?? "";
							const exactCode = isIndiv ? "" : (exact.entityAbn ?? "");
							const exactCodeType: EntityCodeType = exactCode.replace(/\D/g, "").length === 9 ? "ACN" : "ABN";
							setRegistrationData((prev) => ({
								...prev,
								workflow,
								legalEntityCodeType: exactCodeType,
								legalEntityCodeData: exactCode,
								...(prefillFirst ? { firstName: prefillFirst } : {}),
								...(prefillMiddle ? { middleName: prefillMiddle } : {}),
								...(prefillLast ? { lastName: prefillLast } : {}),
								...(prefillEmail ? { email: prefillEmail } : {}),
								...(prefillPhone ? { phone: prefillPhone } : {}),
							}));
							setWizardStep(2);
						}
					}
				})
				.catch(() => {
					setAssessedClients([]);
					setFormAssessment(null);
				})
				.finally(() => setLoadingAssessments(false));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.state]);

	// ── handleOpenInviteForm ─────────────────────────────────────────────
	const handleOpenInviteForm = async () => {
		setShowInviteForm(true);
		setLoadingAssessments(true);
		try {
			const records = await wsApi.riskAssessmentGetAll();
			const mapped = mapRiskRecordsToAssessments(records);
			setAssessedClients(mapped);
			localStorage.setItem(STORAGE_KEY_ASSESSMENTS, JSON.stringify(mapped));
		} catch {
			const stored = localStorage.getItem(STORAGE_KEY_ASSESSMENTS);
			if (stored) {
				try {
					setAssessedClients(JSON.parse(stored));
				} catch {
					setAssessedClients([]);
				}
			}
		} finally {
			setLoadingAssessments(false);
		}
	};

	// ── handleOrgNameChange ──────────────────────────────────────────────
	const handleOrgNameChange = (assessmentId: string) => {
		const assessment = assessedClients.find((a) => a.id === assessmentId);
		if (!assessment) {
			setFormAssessment(null);
			setNewInvite({ email: "", organisationName: "", generatedLink: "" });
			setIsIndividual(false);
			setEntitySubType("");
			setRegistrationData((prev) => ({ ...prev, workflow: "INDIVIDUAL" }));
			setWizardStep(1);
			return;
		}
		setFormAssessment(assessment);
		setNewInvite({
			email: assessment.clientRef || assessment.entityAbn || "",
			organisationName: assessment.clientName,
			generatedLink: "",
		});

		// Determine entity type — fall back to name-based detection if c2EntityType is missing
		const effectiveEntityType = assessment.c2EntityType ||
			(() => {
				if (!assessment.entityAbn) return "individual";
				const name = (assessment.clientName || "").toLowerCase();
				if (name.includes("trust") || name.includes("trustee")) return "trust";
				if (name.includes("partnership") || name.includes("partners")) return "body_corporate";
				if (name.includes("association") || name.includes("co-operative")) return "body_corporate";
				return "body_corporate";
			})();
		const isIndiv = effectiveEntityType.toLowerCase() === "individual";
		const subType = c2EntityTypeToSubType(effectiveEntityType);
		const workflow = c2EntityTypeToWorkflow(effectiveEntityType);
		setIsIndividual(isIndiv);
		setEntitySubType(isIndiv ? "" : subType);

		// Contact pre-fill — always runs regardless of c2EntityType
		const contactParts = (assessment.contactPerson ?? "").trim().split(/\s+/);
		const prefillFirst = isIndiv
			? (assessment.clientName.trim().split(/\s+/)[0] ?? "")
			: (contactParts[0] ?? "");
		const prefillLast = isIndiv
			? assessment.clientName.trim().split(/\s+/).slice(1).join(" ")
			: contactParts.slice(1).join(" ");
		const prefillEmail = assessment.clientRef?.includes("@") ? assessment.clientRef : "";
		const prefillPhone = assessment.contactPhone ?? "";

		const assessmentCode = isIndiv ? "" : (assessment.entityAbn ?? "");
		const assessmentCodeType: EntityCodeType = assessmentCode.replace(/\D/g, "").length === 9 ? "ACN" : "ABN";
		setRegistrationData((prev) => ({
			...prev,
			workflow,
			legalEntityCodeType: assessmentCodeType,
			legalEntityCodeData: assessmentCode,
			...(prefillFirst ? { firstName: prefillFirst } : {}),
			...(prefillLast ? { lastName: prefillLast } : {}),
			...(prefillEmail ? { email: prefillEmail } : {}),
			...(prefillPhone ? { phone: prefillPhone } : {}),
		}));

		setWizardStep(2);
	};

	// ── Real-time subscription ───────────────────────────────────────────
	useRealtimeUpdates({
		onInvitationUpdated: (data: any) => {
			setInvitations((prev) =>
				prev.map((inv) => {
					if (inv.id === data.invitationId) {
						return {
							...inv,
							status: (data.status || inv.status).toLowerCase(),
							...(data.email ? { email: data.email } : {}),
						};
					}
					return inv;
				}),
			);
		},
	});	// ── Mount / store sync (hydrate from store on first render only) ────
	const hydratedRef = useRef(false);
	useEffect(() => {
		if (!hydratedRef.current && storeInvitations.length > 0) {
			hydratedRef.current = true;
			setInvitations(storeInvitations);
			setLoadingInvitations(false);
		}
	}, [storeInvitations]);

	// Fetch assessments once WS is ready
	useEffect(() => {
		if (!wsAuthenticated) return;
		wsApi
			.riskAssessmentGetAll()
			.then((records) => {
				const mapped = mapRiskRecordsToAssessments(records);
				setAssessedClients(mapped);
				localStorage.setItem(STORAGE_KEY_ASSESSMENTS, JSON.stringify(mapped));
			})
			.catch(() => { });
	}, [wsAuthenticated]);

	// Fetch invitations once WS is ready
	useEffect(() => {
		if (!wsAuthenticated) return;
		fetchInvitationsFromBackend();
	}, [wsAuthenticated, fetchInvitationsFromBackend]);

	// Real-time push: new invitations
	useEffect(() => {
		const handler = (data: any) => {
			if (data && data.invitationId) {
				const mapped = mapRecordsToInvitations([data]);
				if (mapped.length) {
					setInvitations((prev) => {
						const exists = prev.some((inv) => inv.id === mapped[0].id);
						if (exists) return prev;
						return [mapped[0], ...prev];
					});
				}
			}
		};
		socketService.on("invitation.created", handler);
		return () => {
			socketService.off("invitation.created", handler);
		};
	}, [mapRecordsToInvitations]);

	// ── Logout ───────────────────────────────────────────────────────────
	const handleLogout = () => navigate("/");

	// ── Filtered list ────────────────────────────────────────────────────
	const filteredInvitations = invitations.filter((invitation) => {
		const statusMatch =
			filterStatus === "all" || invitation.status === filterStatus;
		const searchMatch =
			searchTerm === "" ||
			(invitation.email || "")
				.toLowerCase()
				.includes(searchTerm.toLowerCase()) ||
			(invitation.organisationName || "")
				.toLowerCase()
				.includes(searchTerm.toLowerCase());
		return statusMatch && searchMatch;
	});

	// ── Registration field changes ───────────────────────────────────────
	const handleRegistrationFieldChange = (
		field: keyof typeof registrationData,
		value: string,
	) => {
		setRegistrationData((prev) => ({ ...prev, [field]: value }));
		if (field === "legalEntityCodeData" || field === "legalEntityCodeType") {
			setAbnLookupResult(null);
			setAbnLookupError("");
		}
		if (field === "legalEntityCodeData") {
			const digits = value.replace(/\D/g, "");
			const codeType = registrationData.legalEntityCodeType;
			if (
				(codeType === "ABN" && digits.length === 11) ||
				(codeType === "ACN" && digits.length === 9)
			) {
				setTimeout(() => triggerAbnLookupWithCode(digits, codeType), 0);
			}
		}
	};

	// ── ABN/ACN Lookup ───────────────────────────────────────────────────
	const triggerAbnLookupWithCode = async (
		code: string,
		type: EntityCodeType,
	) => {
		if (type === "ABN" && code.length !== 11) {
			setAbnLookupError("ABN must be 11 digits");
			return;
		}
		if (type === "ACN" && code.length !== 9) {
			setAbnLookupError("ACN must be 9 digits");
			return;
		}
		if (type !== "ABN" && type !== "ACN") {
			setAbnLookupError(`Lookup not available for ${type}`);
			return;
		}

		setIsLookingUp(true);
		setAbnLookupError("");
		setAbnLookupResult(null);

		try {
			const result =
				type === "ABN" ? await lookupABN(code) : await lookupACN(code);
			if (result) {
				setAbnLookupResult(result);
				const wf = austracTypeToWorkflow(result.austracType);
				setRegistrationData((prev) => ({ ...prev, workflow: wf }));
			} else {
				setAbnLookupError(`${type} not found in the business register`);
			}
		} catch {
			setAbnLookupError("Lookup failed — please try again");
		} finally {
			setIsLookingUp(false);
		}
	};

	const triggerAbnLookup = async () => {
		const code = registrationData.legalEntityCodeData.replace(/\D/g, "");
		const type = registrationData.legalEntityCodeType;
		await triggerAbnLookupWithCode(code, type);
	};

	const handleAbnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			triggerAbnLookup();
		}
	};

	const handleEntityTypeChange = (entityType: string) => {
		const workflow = determineWorkflow(entityType);
		setRegistrationData((prev) => ({ ...prev, workflow }));
	};

	// ── Send invitation (create case + registration) ────────────────────
	const handleInviteSend = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!newInvite.organisationName) {
			setInviteMessage("❌ Please select a C2 entity");
			return;
		}
		if (!formAssessment) {
			setInviteMessage(
				"❌ A completed Risk Assessment is required before sending an invite. Please assess this entity first.",
			);
			return;
		}
		if (!registrationData.firstName.trim()) {
			setInviteMessage("❌ First name is required");
			return;
		}
		if (!registrationData.lastName.trim()) {
			setInviteMessage("❌ Last name is required");
			return;
		}
		if (!registrationData.email.trim()) {
			setInviteMessage("❌ Email is required");
			return;
		}
		if (!registrationData.phone.trim()) {
			setInviteMessage("❌ Phone number is required");
			return;
		}
		if (!registrationData.phone.trim().startsWith("+")) {
			setInviteMessage("❌ Phone must be in international format (start with + country code, e.g. +61)");
			return;
		}
		const requiresDob =
			registrationData.workflow === "INDIVIDUAL" ||
			registrationData.workflow === "SOLETRADER";
		if (
			requiresDob &&
			(!registrationData.dateOfBirth ||
				registrationData.dateOfBirth.includes("0000") ||
				registrationData.dateOfBirth.includes("-00"))
		) {
			setInviteMessage(
				"❌ Please select Day, Month and Year for date of birth",
			);
			return;
		}
		const skipAbnAcn = registrationData.workflow === "INDIVIDUAL";
		let cleanedCode = "";
		if (!skipAbnAcn && registrationData.legalEntityCodeData.trim()) {
			cleanedCode = cleanEntityCode(registrationData.legalEntityCodeData);
			if (
				registrationData.legalEntityCodeType === "ABN" &&
				!isValidABN(cleanedCode)
			) {
				setInviteMessage("❌ Invalid ABN format. ABN must be 11 digits.");
				return;
			}
			if (
				registrationData.legalEntityCodeType === "ACN" &&
				!isValidACN(cleanedCode)
			) {
				setInviteMessage("❌ Invalid ACN format. ACN must be 9 digits.");
				return;
			}
		}

		setIsRegistering(true);
		setInviteMessage("");

		try {
			// The DOB is held as YYYY-MM-DD with "0000" / "00" standing in for an unselected
			// dropdown (buildDate in WizardStepTwo), so completeness must be judged per
			// component. This previously tested formattedDob.includes("00") against the
			// concatenated YYYYMMDD string, which also matches legitimate dates: 1980-06-06
			// becomes "19800606", where the year's trailing zero meets the month's leading
			// zero. The field was then dropped from the payload silently, and the validation
			// above had already passed it because that check runs on the dashed form and only
			// looks for "0000" and "-00".
			//
			// The consequence was not cosmetic. The C1's DOB never reached registration, so
			// arcaml_identity.c1_provided_dob was left null and GATE_C2_DOB_MISMATCH could not
			// be evaluated at all — KYCPersistence.validateAndUpdateUserKYCData compares only
			// when the registered DOB is non-empty. The C2 could then supply any DOB at data
			// collection and it was accepted unchallenged, with no flag raised.
			//
			// Affected any year ending in 0 with a single-digit month (1950…2020), October with
			// a single-digit day, and every date in the 2000s.
			const [dobYear, dobMonth, dobDay] =
				registrationData.dateOfBirth.split("-");
			const dobComplete =
				!!dobYear &&
				!!dobMonth &&
				!!dobDay &&
				dobYear !== "0000" &&
				dobMonth !== "00" &&
				dobDay !== "00";
			const formattedDob = dobComplete
				? registrationData.dateOfBirth.replace(/-/g, "")
				: "";
			const enhancedProfile = formAssessment
				? riskRatingToEnhancedProfile(formAssessment.riskRating)
				: 3;

			const createCasePayload: {
				firstName: string;
				middleNames?: string;
				lastName: string;
				email: string;
				phone: string;
				dateOfBirth?: string;
				enhancedProfile: number;
				workflow?: string;
				legalEntityCodeType?: string;
				legalEntityCodeData?: string;
				c1Identifier?: string;
			} = {
				firstName: registrationData.firstName.toUpperCase(),
				...(registrationData.middleName.trim() && {
					middleNames: registrationData.middleName.toUpperCase(),
				}),
				lastName: registrationData.lastName.toUpperCase(),
				email: registrationData.email.toLowerCase(),
				phone: formatPhoneNumber(registrationData.phone),
				enhancedProfile,
				workflow: registrationData.workflow,
				...(formattedDob ? { dateOfBirth: formattedDob } : {}),
				...(user?.c1OrgId ? { c1Identifier: user.c1OrgId } : {}),
				...(!skipAbnAcn &&
					cleanedCode && {
					legalEntityCodeType: registrationData.legalEntityCodeType,
					legalEntityCodeData: cleanedCode,
				}),
			};

			const response = await wsApi.createCase(createCasePayload);
			const regResponse: RegistrationResponse = {
				action: (response.action ?? "ERROR") as RegistrationResponse["action"],
				loginId: response.loginId,
				tempPassword: response.tempPassword,
				statusText: response.statusText ?? "",
			};
			setRegistrationResponse(regResponse);

			if (response.action === "SUCCESS") {
				const uniqueLink = `${window.location.origin}/portal/c2-verification/${response.loginId}`;
				setNewInvite({ ...newInvite, generatedLink: uniqueLink });

				try {
					const saved = await wsApi.invitationCreate(
						newInvite.organisationName,
						uniqueLink,
						registrationData.email,
						{
							recipientPhone: registrationData.phone,
							c2Username: response.loginId,
							c2TempPassword: response.tempPassword,
							entityAbn: formAssessment?.entityAbn,
							assessmentId: formAssessment?.id,
						},
					);

					try {
						await fetchInvitationsFromBackend();
					} catch (_fetchErr) {
						const invitation: Invitation = {
							id: saved.invitationId,
							email: registrationData.email,
							organisationName: saved.entityName,
							role: saved.role,
							sentDate: formatDateShort(saved.sentAt),
							status: saved.status,
							expiryDate: formatDateShort(saved.expiresAt),
							c2Username:
								saved.c2Username ||
								(saved.cddLink ? saved.cddLink.split("/").pop() : undefined),
							tempPassword: response.tempPassword,
						};
						setInvitations([invitation, ...invitations]);
						addInvitationToStore(invitation);
					}
				} catch (_wsErr) {
					const invitation: Invitation = {
						id: Date.now().toString(),
						email: registrationData.email,
						organisationName: newInvite.organisationName,
						role: "Pending Verification",
						sentDate: formatDateShort(new Date()),
						status: "pending",
						expiryDate: formatDateShort(
							new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
						),
						c2Username: response.loginId,
						tempPassword: response.tempPassword,
					};
					setInvitations([invitation, ...invitations]);
					addInvitationToStore(invitation);
				}

				setInviteMessage("✅ Customer registered successfully");
			} else {
				setInviteMessage(`❌ Registration failed: ${response.statusText}`);
			}
		} catch (_err) {
			setInviteMessage("❌ Failed to register user. Please try again.");
		} finally {
			setIsRegistering(false);
		}
	};

	// ── Regenerate link ──────────────────────────────────────────────────
	const handleRegenerateLink = async (id: string) => {
		const invitation = invitations.find((inv) => inv.id === id);
		if (invitation) {
			const newLink = `${window.location.origin}/portal/c2-verification/${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
			setNewInvite({
				...newInvite,
				email:
					invitation.email && invitation.email !== "N/A"
						? invitation.email
						: "",
				organisationName: invitation.organisationName,
				generatedLink: newLink,
			});
			const exact =
				assessedClients.find(
					(a) => a.clientName === invitation.organisationName,
				) ?? null;
			setFormAssessment(exact);
			setShowInviteForm(true);

			try {
				await wsApi.invitationUpdate(id, { cddLink: newLink });
			} catch (_err) { }

			setInvitations(
				invitations.map((inv) =>
					inv.id === id
						? {
							...inv,
							sentDate: formatDateShort(new Date()),
							expiryDate: formatDateShort(
								new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
							),
							status: "pending",
						}
						: inv,
				),
			);

			setInviteMessage(
				`✅ Form pre-filled with ${invitation.organisationName} data. New link generated. Valid for 30 days.`,
			);
			setTimeout(() => setInviteMessage(""), 3000);

			setTimeout(() => {
				const formElement = document.getElementById("invite-form");
				if (formElement) {
					formElement.scrollIntoView({ behavior: "smooth", block: "start" });
				}
			}, 100);
		}
	};

	// ── Cancel invitation ────────────────────────────────────────────────
	const handleCancelInvitation = (id: string) => {
		setInvitations(invitations.filter((inv) => inv.id !== id));
		setInviteMessage("✅ Invitation cancelled");
		setTimeout(() => setInviteMessage(""), 3000);
	};

	// ── Copy link ────────────────────────────────────────────────────────
	const handleCopyLink = () => {
		if (newInvite.generatedLink) {
			navigator.clipboard.writeText(newInvite.generatedLink);
			setInviteMessage("✅ Link copied to clipboard!");
			setTimeout(() => setInviteMessage(""), 2000);
		}
	};

	// ── Register another contact for the same entity ────────────────────
	const handleRegisterAnother = () => {
		setRegistrationResponse(null);
		setInviteMessage("");
		setNewInvite((prev) => ({ ...prev, generatedLink: "" }));
		setRegistrationData((prev) => ({
			...prev,
			firstName: "",
			middleName: "",
			lastName: "",
			email: "",
			phone: "",
			dateOfBirth: "",
		}));
		setWizardStep(2);
	};

	// ── Close / reset form ───────────────────────────────────────────────
	const handleCloseForm = () => {
		setShowInviteForm(false);
		setNewInvite({ email: "", organisationName: "", generatedLink: "" });
		setRegistrationData({
			firstName: "",
			middleName: "",
			lastName: "",
			email: "",
			phone: "",
			dateOfBirth: "",
			legalEntityCodeType: "ABN",
			legalEntityCodeData: "",
			workflow: "INDIVIDUAL",
		});
		setRegistrationResponse(null);
		setInviteMessage("");
		setFormAssessment(null);
		setIsIndividual(false);
		setWizardStep(1);
		setEntitySubType("");
	};

	// ── Status badge config ──────────────────────────────────────────────
	const getStatusBadge = (status: string) => {
		const statusConfig = {
			pending: { color: "#ffc107", icon: "⏳", label: "Pending" },
			accepted: { color: "#28a745", icon: "✅", label: "Accepted" },
			declined: { color: "#dc3545", icon: "❌", label: "Declined" },
			expired: { color: "#6c757d", icon: "⚠️", label: "Expired" },
		};
		const config = statusConfig[status as keyof typeof statusConfig] || {
			color: "#999999",
			icon: "❓",
			label: "Unknown",
		};
		return { ...config };
	};

	return {
		// State
		invitations,
		loadingInvitations,
		showInviteForm,
		newInvite,
		registrationData,
		registrationResponse,
		isRegistering,
		inviteMessage,
		filterStatus,
		searchTerm,
		formAssessment,
		assessedClients,
		loadingAssessments,
		isLookingUp,
		abnLookupResult,
		abnLookupError,
		isIndividual,
		wizardStep,
		entitySubType,
		filteredInvitations,

		// Setters
		setInviteMessage,
		setFilterStatus,
		setSearchTerm,
		setWizardStep,

		// Actions
		handleLogout,
		handleOpenInviteForm,
		handleCloseForm,
		handleRegisterAnother,
		handleOrgNameChange,
		handleRegistrationFieldChange,
		handleAbnKeyDown,
		handleEntityTypeChange,
		handleInviteSend,
		handleRegenerateLink,
		handleCancelInvitation,
		handleCopyLink,
		triggerAbnLookup,
		getStatusBadge,

		// Re-exports for JSX usage
		RISK_BADGE_CONFIG,
		formatDateShort,
		formatABN,
		formatACN,
		findAssessmentInList,
	};
}

export type InvitationsState = ReturnType<typeof useInvitationsState>;
